import { useEffect, useMemo, useState } from 'react'
import { Download, Plus, Search, Trash2, X } from 'lucide-react'
import {
  ApiError,
  CartData,
  postCreatorCart,
  postCreatorCartExcel,
} from './creatorBusiness'
import type { CreatorCart } from './useCreatorCart'

export default function EstimateCalculatorModal({
  open,
  onOpenChange,
  cart,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  cart: CreatorCart
}) {
  const { creators, addCreators, removeCreator, setProductItems, clear } = cart
  const [cartData, setCartData] = useState<CartData | null>(null)
  const [keyword, setKeyword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)

  // 미선택(빈 key) 항목은 서버로 보내지 않음
  const toRequest = (cs: typeof creators) =>
    cs.map((c) => ({ ...c, productItems: c.productItems.filter((p) => p.key) }))

  // 열렸을 때 + creators 변경 시 카트 재조회 (연속 편집 디바운스)
  useEffect(() => {
    if (!open) return
    setError(null)
    const t = setTimeout(() => {
      setLoading(true)
      postCreatorCart({ creators: toRequest(creators) })
        .then(setCartData)
        .catch((e) => setError((e as ApiError).message))
        .finally(() => setLoading(false))
    }, 250)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, creators])

  const bodyKeys = (id: string): string[] =>
    (creators.find((c) => c.salesforceCreatorId === id)?.productItems ?? []).map((p) => p.key)

  const searchResults = useMemo(() => {
    const k = keyword.trim().toLowerCase()
    if (!k || !cartData) return []
    const added = new Set(creators.map((c) => c.salesforceCreatorId))
    return cartData.selectableCreators
      .filter((c) => !added.has(c.salesforceId))
      .filter((c) => c.name.toLowerCase().includes(k))
      .slice(0, 8)
  }, [keyword, cartData, creators])

  const entries = useMemo(
    () =>
      creators.map((bodyC) => {
        const d = cartData?.creators.find((x) => x.salesforceId === bodyC.salesforceCreatorId)
        return {
          id: bodyC.salesforceCreatorId,
          name: d?.name ?? bodyC.salesforceCreatorId,
          imageUrl: d?.imageUrl,
          subtotal: d?.subtotal ?? '',
          selectable: d?.selectableProductItems ?? [],
          rows: bodyC.productItems.map((pi) => ({
            key: pi.key,
            price: d?.productItems.find((p) => p.key === pi.key)?.price ?? '',
          })),
        }
      }),
    [creators, cartData],
  )

  if (!open) return null

  const handleExport = async () => {
    if (creators.length === 0) return
    setExporting(true)
    try {
      await postCreatorCartExcel({ creators: toRequest(creators) })
    } catch (e) {
      setError((e as ApiError).message)
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-brand-brown/40" onClick={() => onOpenChange(false)} />
      <div className="relative flex max-h-[85vh] w-full max-w-2xl flex-col rounded-r-2 bg-surface-base shadow-lf-pop">
        {/* header */}
        <div className="flex items-center justify-between border-b border-line-soft px-5 py-4">
          <div>
            <div className="lf-h4 text-fg-1">견적 계산기</div>
            <div className="lf-caption text-fg-3">
              {cartData ? `크리에이터 ${cartData.creatorCount} · 항목 ${cartData.productCount}` : ' '}
            </div>
          </div>
          <button type="button" onClick={() => onOpenChange(false)} className="text-fg-3 hover:text-fg-1">
            <X size={18} />
          </button>
        </div>

        {/* add creator search */}
        <div className="border-b border-line-hairline px-5 py-3">
          <div className="relative">
            <Search size={15} className="absolute left-2.5 top-2.5 text-fg-3" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="크리에이터 검색해서 추가"
              className="w-full rounded-r-1 border border-line-soft bg-surface-base py-2 pl-8 pr-3 lf-body-sm text-fg-1"
            />
            {searchResults.length > 0 && (
              <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-r-1 border border-line-soft bg-surface-base shadow-lf-pop">
                {searchResults.map((c) => (
                  <button
                    key={c.salesforceId}
                    type="button"
                    onClick={() => {
                      addCreators([{ salesforceCreatorId: c.salesforceId, productItems: [{ key: '' }] }])
                      setKeyword('')
                    }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left lf-body-sm text-fg-1 hover:bg-surface-soft"
                  >
                    <Plus size={13} className="text-brand-pink" /> {c.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* body */}
        <div className="flex-1 overflow-auto px-5 py-3">
          {error && <div className="mb-2 lf-body-sm text-brand-pink">{error}</div>}
          {entries.length === 0 && (
            <div className="py-10 text-center lf-body-sm text-fg-3">담긴 크리에이터가 없습니다.</div>
          )}

          {entries.map((e) => (
            <div key={e.id} className="mb-3 rounded-r-1 border border-line-soft p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  {e.imageUrl && e.imageUrl !== '-' ? (
                    <img src={e.imageUrl} alt={e.name} className="h-6 w-6 rounded-full object-cover" />
                  ) : (
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-surface-muted text-[11px] text-fg-2">
                      {e.name?.[0] ?? '?'}
                    </span>
                  )}
                  <span className="truncate lf-body-sm font-medium text-fg-1">{e.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="lf-body-sm lf-num text-fg-1">{e.subtotal || '-'}</span>
                  <button type="button" onClick={() => removeCreator(e.id)} className="text-fg-3 hover:text-brand-pink">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>

              {e.rows.map((row, idx) => (
                <div key={idx} className="mb-1 flex items-center gap-2">
                  <select
                    value={row.key}
                    onChange={(ev) => {
                      const keys = bodyKeys(e.id)
                      keys[idx] = ev.target.value
                      setProductItems(e.id, keys)
                    }}
                    className="flex-1 rounded-r-1 border border-line-soft bg-surface-base px-2 py-1 lf-body-sm text-fg-1"
                  >
                    <option value="">항목 선택</option>
                    {e.selectable.map((s) => (
                      <option key={s.key} value={s.key}>{s.name}</option>
                    ))}
                  </select>
                  <span className="w-28 text-right lf-body-sm lf-num text-fg-2">{row.price || '-'}</span>
                  <button
                    type="button"
                    onClick={() => setProductItems(e.id, bodyKeys(e.id).filter((_, i) => i !== idx))}
                    className="text-fg-3 hover:text-brand-pink"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}

              <button
                type="button"
                onClick={() => setProductItems(e.id, [...bodyKeys(e.id), ''])}
                className="mt-1 flex items-center gap-1 lf-caption text-brand-blue hover:opacity-80"
              >
                <Plus size={12} /> 항목 추가
              </button>
            </div>
          ))}
        </div>

        {/* footer */}
        <div className="flex items-center justify-between gap-3 border-t border-line-soft px-5 py-4">
          <div className="flex items-center gap-3">
            <span className="lf-caption text-fg-3">합계</span>
            <span className="lf-h4 lf-num text-fg-1">{cartData?.total ?? '-'}</span>
            {loading && <span className="lf-caption text-fg-3">계산 중…</span>}
          </div>
          <div className="flex items-center gap-2">
            {entries.length > 0 && (
              <button type="button" onClick={clear} className="lf-caption text-fg-3 hover:text-fg-1">
                전체 비우기
              </button>
            )}
            <button
              type="button"
              onClick={handleExport}
              disabled={entries.length === 0 || exporting}
              className="flex items-center gap-1.5 rounded-r-1 bg-brand-blue px-3 py-2 lf-body-sm font-medium text-fg-inverse hover:opacity-90 disabled:opacity-40"
            >
              <Download size={15} /> {exporting ? '추출 중…' : '엑셀 추출'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
